'use client'

import { BondType } from '@moment/trading-types/reference-data'
import { type RefObject, useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { IconName } from '@/components/atomic/icon'
import ModelConstraintCard from '@/components/molecules/model-constraint-card'
import ModelSectionCard from '@/components/molecules/model-section-card'
import {
  TargetBand,
  type TargetBandRef,
  TargetBandType,
} from '@/components/molecules/target-band'
import {
  ResetButton,
  TargetCardContent,
} from '@/components/molecules/target-band/styles'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { type ModelBuilder, setModelField } from '@/reducers/model-builder'
import { ValueUnit } from '@/types/enums/value-unit'

import { DEFAULT_TARGET_BAND_STATE } from './default-values'

export type TargetAverageKeys = Extract<
  keyof ModelBuilder,
  | 'spRatingTargetBand'
  | 'lowestRatingTargetBand'
  | 'moodysRatingTargetBand'
  | 'couponTargetBand'
  | 'durationTargetBand'
  | 'convexityTargetBand'
  | 'liquidityScoreTargetBand'
  | 'maturityTargetBand'
>

export enum TargetAverageApiField {
  SpRating = 'sp_rating',
  LowestRating = 'lowest_rating',
  MoodysRating = 'moodys_rating',
  Coupon = 'coupon',
  Duration = 'duration',
  LiquidityScore = 'liquidity_institutional_aggregate',
  Convexity = 'convexity',
  Maturity = 'maturity_in_years',
}

export const TARGET_AVERAGE_KEYS_TO_API_FIELD: Record<
  TargetAverageKeys,
  TargetAverageApiField
> = {
  spRatingTargetBand: TargetAverageApiField.SpRating,
  lowestRatingTargetBand: TargetAverageApiField.LowestRating,
  moodysRatingTargetBand: TargetAverageApiField.MoodysRating,
  couponTargetBand: TargetAverageApiField.Coupon,
  durationTargetBand: TargetAverageApiField.Duration,
  liquidityScoreTargetBand: TargetAverageApiField.LiquidityScore,
  convexityTargetBand: TargetAverageApiField.Convexity,
  maturityTargetBand: TargetAverageApiField.Maturity,
}

export const nonApplicableTargetAverageFields: Record<
  BondType,
  TargetAverageKeys[]
> = {
  [BondType.Agency]: [],
  [BondType.CD]: [],
  [BondType.Corporate]: [],
  [BondType.Government]: [],
  [BondType.MoneyMarket]: [],
  [BondType.Municipal]: [],
  [BondType.Securitized]: [],
  [BondType.Treasury]: [
    'spRatingTargetBand',
    'lowestRatingTargetBand',
    'moodysRatingTargetBand',
  ],
}

function TargetAveragesTab() {
  const model = useTieredSelector(
    (state) => state.modelBuilder?.model,
  ) as ModelBuilder

  // Check if a field is applicable to at least one selected asset class (UNION logic)
  const isFieldApplicable = useCallback(
    (field: TargetAverageKeys) => {
      const assetClasses = model?.assetClasses || []
      return assetClasses.some(
        (assetClass) =>
          !nonApplicableTargetAverageFields[assetClass].includes(field),
      )
    },
    [model?.assetClasses],
  )

  const spRatingRef = useRef<TargetBandRef>(null)
  const lowestRatingRef = useRef<TargetBandRef>(null)
  const moodysRatingRef = useRef<TargetBandRef>(null)
  const couponRef = useRef<TargetBandRef>(null)
  const durationRef = useRef<TargetBandRef>(null)
  const convexityRef = useRef<TargetBandRef>(null)
  const liquidityScoreRef = useRef<TargetBandRef>(null)
  const maturityRef = useRef<TargetBandRef>(null)

  const refs: Record<
    TargetAverageKeys,
    RefObject<TargetBandRef | null>
  > = useMemo(
    () => ({
      spRatingTargetBand: spRatingRef,
      lowestRatingTargetBand: lowestRatingRef,
      moodysRatingTargetBand: moodysRatingRef,
      couponTargetBand: couponRef,
      durationTargetBand: durationRef,
      convexityTargetBand: convexityRef,
      liquidityScoreTargetBand: liquidityScoreRef,
      maturityTargetBand: maturityRef,
    }),
    [],
  )

  const dispatch = useDispatch()

  const handleFieldChange = useCallback(
    <T extends keyof ModelBuilder>(field: T, value: ModelBuilder[T]) => {
      dispatch(setModelField({ field, value }))
    },
    [dispatch],
  )

  const handleReset = useCallback(
    <T extends ModelBuilder[TargetAverageKeys]>(
      field: TargetAverageKeys,
      defaultValue: T,
    ) =>
      () => {
        refs[field].current?.setTargetBandState(DEFAULT_TARGET_BAND_STATE)
        dispatch(setModelField({ field, value: defaultValue }))
      },
    [dispatch, refs],
  )

  const resetButton = useCallback(
    (field: TargetAverageKeys) => (
      <ResetButton onClick={handleReset(field, DEFAULT_TARGET_BAND_STATE)} />
    ),
    [handleReset],
  )

  return (
    <ModelSectionCard
      title="Target averages"
      description="Define the desired target averages across the portfolio."
      icon={IconName.ViewfinderCircleIcon}
      iconColor="text-moment-blue-700 dark:text-moment-blue-400"
      iconBackgroundColor="bg-moment-blue-100 dark:bg-moment-blue-900"
    >
      <TargetCardContent>
        {isFieldApplicable('spRatingTargetBand') && (
          <ModelConstraintCard
            title="S&P rating target average"
            tooltipText="S&P rating target average"
            rightHeaderContent={resetButton('spRatingTargetBand')}
          >
            <TargetBand
              ref={refs.spRatingTargetBand}
              type={TargetBandType.Select}
              initial={model?.spRatingTargetBand}
              onChange={(state) =>
                handleFieldChange('spRatingTargetBand', state)
              }
            />
          </ModelConstraintCard>
        )}
        {isFieldApplicable('lowestRatingTargetBand') && (
          <ModelConstraintCard
            title="Lowest rating target average"
            tooltipText="Lowest rating target average"
            rightHeaderContent={resetButton('lowestRatingTargetBand')}
          >
            <TargetBand
              ref={refs.lowestRatingTargetBand}
              type={TargetBandType.Select}
              initial={model?.lowestRatingTargetBand}
              onChange={(state) =>
                handleFieldChange('lowestRatingTargetBand', state)
              }
            />
          </ModelConstraintCard>
        )}
        {isFieldApplicable('moodysRatingTargetBand') && (
          <ModelConstraintCard
            title="Moody's rating target average"
            tooltipText="Moody's rating target average"
            rightHeaderContent={resetButton('moodysRatingTargetBand')}
          >
            <TargetBand
              ref={refs.moodysRatingTargetBand}
              type={TargetBandType.MoodysSelect}
              initial={model?.moodysRatingTargetBand}
              onChange={(state) =>
                handleFieldChange('moodysRatingTargetBand', state)
              }
            />
          </ModelConstraintCard>
        )}
        {isFieldApplicable('couponTargetBand') && (
          <ModelConstraintCard
            title="Coupon target average"
            tooltipText="Coupon target average"
            rightHeaderContent={resetButton('couponTargetBand')}
          >
            <TargetBand
              ref={refs.couponTargetBand}
              initial={model?.couponTargetBand}
              onChange={(state) => handleFieldChange('couponTargetBand', state)}
              unit={ValueUnit.Percentage}
            />
          </ModelConstraintCard>
        )}
        {isFieldApplicable('durationTargetBand') && (
          <ModelConstraintCard
            title="Duration target average"
            tooltipText="Duration target average"
            rightHeaderContent={resetButton('durationTargetBand')}
          >
            <TargetBand
              ref={refs.durationTargetBand}
              initial={model?.durationTargetBand}
              onChange={(state) =>
                handleFieldChange('durationTargetBand', state)
              }
              unit={ValueUnit.Years}
            />
          </ModelConstraintCard>
        )}
        {isFieldApplicable('convexityTargetBand') && (
          <ModelConstraintCard
            title="Convexity target average"
            tooltipText="Convexity target average"
            rightHeaderContent={resetButton('convexityTargetBand')}
          >
            <TargetBand
              ref={refs.convexityTargetBand}
              initial={model?.convexityTargetBand}
              onChange={(state) =>
                handleFieldChange('convexityTargetBand', state)
              }
              unit={ValueUnit.Percentage}
            />
          </ModelConstraintCard>
        )}
        {isFieldApplicable('liquidityScoreTargetBand') && (
          <ModelConstraintCard
            title="Liquidity score target average"
            tooltipText="Liquidity score target average"
            rightHeaderContent={resetButton('liquidityScoreTargetBand')}
          >
            <TargetBand
              ref={refs.liquidityScoreTargetBand}
              initial={model?.liquidityScoreTargetBand}
              onChange={(state) =>
                handleFieldChange('liquidityScoreTargetBand', state)
              }
              unit={ValueUnit.Stars}
            />
          </ModelConstraintCard>
        )}
        {isFieldApplicable('maturityTargetBand') && (
          <ModelConstraintCard
            title="Maturity target average"
            tooltipText="Maturity target average"
            rightHeaderContent={resetButton('maturityTargetBand')}
          >
            <TargetBand
              ref={refs.maturityTargetBand}
              initial={model?.maturityTargetBand}
              onChange={(state) =>
                handleFieldChange('maturityTargetBand', state)
              }
              unit={ValueUnit.Years}
            />
          </ModelConstraintCard>
        )}
      </TargetCardContent>
    </ModelSectionCard>
  )
}

// Dead UI retained for live constants in this file; unexported for knip.
void TargetAveragesTab
